import { supabase } from '@/integrations/supabase/client';
import type { Scene, Character, Location } from '@/types/scriptAnalysis';
import type { StageSignals } from '@/lib/stage-progress';
import { applyLegalParams } from '@/lib/legal-params';
import { fetchLegalParamValues } from '@/services/legalParamsService';
import { fetchAllJobBudgetLines } from '@/lib/budget-lines-query';
import {
  estadoDeAplicacion, resumenDeAplicacion, construirEntradaLogChat,
  type SubAccionLog, type ResultadoAcciones,
} from '@/lib/chat-apply-estado';

/** Error de applyChatActions con el recuento adjunto, para que la interfaz pueda
 *  decir qué se aplicó y qué no en vez de una fórmula genérica. */
export interface ErrorDeAplicacion extends Error {
  resultado: ResultadoAcciones;
  detalle: string;
}
// Worker de pdfjs vía Vite (?url): empaqueta el worker del MISMO paquete instalado,
// con type:module correcto. La ruta estática '/pdf.worker.min.mjs' fallaba en Safari
// (es estricto cargando workers en módulo) → 0 texto → fallback OCR servidor → job colgado.
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

export async function extractTextFromPdf(file: File): Promise<string> {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pageTexts: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    // Reconstruir líneas con hasEOL. Antes se unía TODO con espacios → texto
    // "run-on" sin saltos, que rompía el formato de guion (cabecera/personaje/
    // diálogo en una sola línea). Con los saltos, el fragmento sale como guion.
    let text = '';
    for (const item of content.items as Array<{ str?: string; hasEOL?: boolean }>) {
      if (typeof item.str !== 'string') continue;
      text += item.str + (item.hasEOL ? '\n' : ' ');
    }
    text = text.split('\n').map((l) => l.replace(/[ \t]+/g, ' ').trim()).join('\n');
    pageTexts.push(text);
  }

  return pageTexts.join('\n\n--- PAGE BREAK ---\n\n');
}

export interface StartAnalysisOptions {
  formato?: 'corto' | 'largo' | 'serie';
  tipo?: 'ficcion' | 'documental';
  /** Solo en serie: nº de capítulos de la temporada que se suben. Es un dato que solo
   *  tiene el usuario, así que viaja desde el selector de subida hasta el job; el motor
   *  construye el presupuesto de UN capítulo y lo multiplica por este número. */
  numCapitulos?: number;
  /** 'guion' (default) = flujo normal con escenas. 'tratamiento' = asistente de escritura (prosa → secuenciado → escenas). */
  sourceDocType?: 'guion' | 'tratamiento';
  /** V3 · si viene, este análisis es una NUEVA VERSIÓN del job parentJobId
   *  (reutiliza lo no cambiado; NO consume plaza de plan). */
  parentJobId?: string;
}

// Resuelve con la promesa o rechaza al cruzar `ms`. La tarea original puede
// seguir en background (pdfjs no se puede cancelar limpio), pero no nos bloquea.
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`timeout ${ms}ms`)), ms)),
  ]);
}

export async function startAnalysis(file: File, opts: StartAnalysisOptions = {}): Promise<string> {
  // Garantizar sesión válida ANTES de invocar. Si el token caducó (p.ej. pestaña
  // inactiva >1h sin refresco), supabase-js enviaría la anon key → el server
  // respondería 401 y el análisis se perdería (barra eterna). Refrescamos si está
  // por caducar; si la sesión no es recuperable, cerramos sesión (AuthGate lleva
  // a /login) y avisamos con un mensaje claro.
  const { data: { session } } = await supabase.auth.getSession();
  let validSession = session;
  if (session?.expires_at && session.expires_at * 1000 < Date.now() + 60_000) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    validSession = refreshed.session;
  }
  if (!validSession) {
    await supabase.auth.signOut().catch(() => {});
    throw new Error('Tu sesión caducó. Vuelve a iniciar sesión para analizar el guion.');
  }

  const fileType = getFileType(file.name);
  const formData = new FormData();

  if (fileType === 'pdf') {
    // Extracción en cliente (pdfjs). Es frágil: ciertos PDF (escaneados,
    // protegidos, raros) o navegadores la CUELGAN, dejando el análisis "trabado"
    // sin crear job (caso Edurne · TRANSFERENCIA). TIMEOUT + fallback: si falla o
    // tarda demasiado, mandamos el PDF CRUDO y el servidor lo extrae (pdf-lib +
    // OCR Claude). Así el job SIEMPRE se crea y la barra aparece.
    let extractedText = '';
    try {
      // QW3 (robustez guiones largos): el timeout de extracción en cliente escala con el
      // tamaño del PDF. Un guion de temporada (~250 páginas) puede tardar más de 45s en
      // pdfjs sobre un portátil lento y caía al camino frágil del servidor (isolate 256MB,
      // que es donde murió el PDF de Sara). Escalamos 45s + 15s por MB, con techo 180s.
      // Los PDF pequeños siguen con ~45s (sin degradación).
      const pdfTimeoutMs = Math.min(45_000 + Math.round(file.size / (1024 * 1024)) * 15_000, 180_000);
      extractedText = await withTimeout(extractTextFromPdf(file), pdfTimeoutMs);
    } catch (e) {
      console.warn('[startAnalysis] extracción PDF en cliente falló/timeout → fallback servidor:', e);
      extractedText = '';
    }

    if (extractedText.trim().length > 200) {
      // Send pre-extracted text as a .txt blob
      const textBlob = new Blob([extractedText], { type: 'text/plain' });
      formData.append('file', textBlob, file.name);
      formData.append('file_type', 'fountain'); // text-based, no parsing needed
      formData.append('original_file_type', 'pdf');
    } else {
      // pdfjs couldn't extract — send the PDF binary as fallback
      formData.append('file', file);
      formData.append('file_type', fileType);
    }
  } else {
    formData.append('file', file);
    formData.append('file_type', fileType);
  }

  if (opts.formato) formData.append('formato', opts.formato);
  if (opts.tipo) formData.append('tipo', opts.tipo);
  // Solo tiene sentido en serie; el servidor lo ignora en corto/largo (y guarda NULL).
  if (opts.formato === 'serie' && opts.numCapitulos != null) {
    formData.append('num_capitulos', String(opts.numCapitulos));
  }
  if (opts.sourceDocType) formData.append('source_doc_type', opts.sourceDocType);
  if (opts.parentJobId) formData.append('parent_job_id', opts.parentJobId);

  const { data, error } = await supabase.functions.invoke('analyze-script', {
    body: formData,
  });

  if (error) throw new Error(error.message || 'Error al iniciar el analisis');
  return data.job_id;
}

export async function continueAnalysis(jobId: string): Promise<string> {
  const formData = new FormData();
  formData.append('continue_job', jobId);

  const { data, error } = await supabase.functions.invoke('analyze-script', {
    body: formData,
  });

  if (error) throw new Error(error.message || 'Error al continuar el análisis');
  return data.job_id;
}

export async function fetchAnalysisJobs() {
  const { data, error } = await supabase
    .from('analysis_jobs')
    .select('id, user_id, status, stage, progress, file_name, file_type, total_scenes, scenes_processed, created_at, updated_at, usage_cost_cents, archived_at, formato, tipo, num_capitulos, project_id, version, parent_job_id, reanalysis_mode, diff_summary')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function updateAnalysisJob(jobId: string, updates: {
  formato?: 'corto' | 'largo' | 'serie' | string;
  tipo?: 'ficcion' | 'documental' | string;
  num_capitulos?: number | null;
}) {
  const { data, error } = await supabase
    .from('analysis_jobs')
    .update(updates)
    .eq('id', jobId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function fetchAnalysisJob(jobId: string) {
  const { data, error } = await supabase
    .from('analysis_jobs')
    .select('id, status, stage, progress, file_name, file_type, total_scenes, scenes_processed, created_at, updated_at, usage_cost_cents, formato, tipo, num_capitulos, locations_consolidated_at, project_id, version, parent_job_id, reanalysis_mode, diff_summary')
    .eq('id', jobId)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteAnalysisJob(jobId: string): Promise<void> {
  // 1) Marcar como cancelled — corta cualquier edge-function que esté escribiendo.
  //    Si está `processing`, esto evita que UPDATE concurrentes del background
  //    re-creen filas mientras hacemos el DELETE.
  await supabase.from('analysis_jobs')
    .update({ status: 'cancelled', error_message: 'Eliminado por usuario' })
    .eq('id', jobId);

  // 2) Borrar todas las dependencias explícitas. La mayoría tienen
  //    ON DELETE CASCADE, pero las listamos por si alguna no lo tiene
  //    (failsafe + acelera el delete final).
  const tables = [
    'budget_lines', 'budget_params', 'budget_estimates', 'budget_estimation_jobs',
    'budget_action_log', 'budget_complexity_flags', 'budget_audits',
    'project_content', 'chat_messages',
    'emotional_map', 'narrative_diagnostics', 'casting_profiles', 'themes',
    'conflicts', 'character_arcs', 'narrative_analysis',
    'distribution_strategy', 'scene_cast', 'scenes',
    'characters', 'locations', 'character_scenes',
    'pipeline_stages',
  ];
  const errors: string[] = [];
  for (const table of tables) {
    const { error } = await supabase.from(table).delete().eq('job_id', jobId);
    if (error && !/does not exist/i.test(error.message)) {
      errors.push(`${table}: ${error.message}`);
    }
  }

  // 3) DELETE final del job
  const { error: jobErr } = await supabase.from('analysis_jobs').delete().eq('id', jobId);
  if (jobErr) {
    errors.push(`analysis_jobs: ${jobErr.message}`);
  }

  if (errors.length > 0) {
    throw new Error(`Borrado parcial. Errores:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n…y ${errors.length - 5} más` : ''}`);
  }
}

export async function cancelAnalysis(jobId: string): Promise<void> {
  const { error } = await supabase
    .from('analysis_jobs')
    .update({ status: 'cancelled' })
    .eq('id', jobId);
  if (error) throw new Error(error.message || 'Error al cancelar el analisis');
}

export async function fetchScenes(jobId: string): Promise<Scene[]> {
  const { data, error } = await supabase
    .from('scenes')
    .select('*')
    .eq('job_id', jobId)
    .order('scene_number', { ascending: true });
  if (error) throw new Error(error.message);
  return data as Scene[];
}

export async function updateScene(sceneId: string, updates: {
  raw_text?: string;
  header?: string;
  synopsis?: string;
  scene_number?: number;
  status?: 'draft' | 'confirmed' | 'rejected';
  has_vehicle_travel?: boolean;
  vehicle_travel_origin?: string | null;
  vehicle_travel_destination?: string | null;
  dia_ficcion?: string | null;
  breakdown?: Record<string, unknown>;
}) {
  const { data, error } = await supabase
    .from('scenes')
    .update(updates)
    .eq('id', sceneId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Borra una escena. Todo el trabajo lo hace la función `borrar_escena` de la base, en una
 * sola transacción: comprueba el permiso, borra, renumera las siguientes y deja cuadrado
 * `analysis_jobs.total_scenes`.
 *
 * La primera versión lo hacía aquí en tres pasos y tenía un fallo grave: la política de
 * DELETE de `scenes` solo la permite al DUEÑO del job, mientras el UPDATE la permite a
 * cualquier miembro de la organización. Un colaborador pulsaba la papelera y la escena NO se
 * borraba (cero filas, sin error), pero el renumerado SÍ se aplicaba: las posteriores bajaban
 * un número, quedaban duplicados y la interfaz decía "escena eliminada".
 */
export async function deleteScene(sceneId: string): Promise<{ renumeradas: number }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('borrar_escena', { p_scene: sceneId });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as { renumeradas?: number };
  return { renumeradas: Number(r.renumeradas ?? 0) };
}

export async function saveScriptSnapshot(jobId: string, scenes: Array<{
  scene_number: number;
  header: string;
  raw_text: string | null;
  synopsis: string | null;
}>) {
  const snapshot = {
    job_id: jobId,
    content_type: 'script_snapshot',
    content: JSON.stringify({
      timestamp: new Date().toISOString(),
      scene_count: scenes.length,
      scenes: scenes.map(s => ({
        scene_number: s.scene_number,
        header: s.header,
        raw_text: s.raw_text,
        synopsis: s.synopsis,
      })),
    }),
  };
  // Use upsert with a versioned content_type
  const versionKey = `script_snapshot_${new Date().toISOString().replace(/[:.]/g, '-')}`;
  const { error } = await supabase.from('project_content').insert({
    ...snapshot,
    content_type: versionKey,
  });
  if (error) throw new Error(error.message);
}

export async function fetchScriptSnapshots(jobId: string) {
  const { data, error } = await supabase
    .from('project_content')
    .select('*')
    .eq('job_id', jobId)
    .like('content_type', 'script_snapshot_%')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchCharacters(jobId: string): Promise<Character[]> {
  const { data: characters, error: charError } = await supabase
    .from('characters')
    .select('*, character_scenes(scene_id)')
    .eq('job_id', jobId)
    .order('name', { ascending: true });
  if (charError) throw new Error(charError.message);
  return (characters || []).map((c: any) => ({
    ...c,
    scene_ids: (c.character_scenes || []).map((cs: any) => cs.scene_id),
  }));
}

// Reparto agregado (scene_cast): nº de líneas de diálogo por personaje. Fuente de la
// señal REAL de diálogo del diagnóstico narrativo (nº de voces, reparto de la carga).
export type AggregatedCastRow = { name: string; type: string; dialogue_lines: number; scene_count: number | null; scenes: number[] | null };

export async function fetchAggregatedCast(jobId: string): Promise<AggregatedCastRow[]> {
  const { data, error } = await supabase
    .from('scene_cast')
    .select('name, type, dialogue_lines, scene_count, scenes')
    .eq('job_id', jobId);
  if (error) throw new Error(error.message);
  return (data || []) as AggregatedCastRow[];
}

export async function fetchLocations(jobId: string): Promise<Location[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('*')
    .eq('job_id', jobId)
    .order('name', { ascending: true });
  if (error) throw new Error(error.message);
  const locations = data as Location[];
  const parents = locations.filter(l => !l.parent_id);
  return parents.map(p => ({
    ...p,
    variants: locations.filter(l => l.parent_id === p.id),
  }));
}

export async function updateLocationGeo(locationId: string, geo: {
  lat?: number;
  lng?: number;
  address?: string;
  place_id?: string;
  space_type?: 'natural' | 'studio' | 'built_set';
}) {
  const { error } = await supabase
    .from('locations')
    .update(geo)
    .eq('id', locationId);
  if (error) throw new Error(error.message);
}

// === Location · canonical CRUD (Localizador stage) ===
// updateLocation acepta cualquier campo de la tabla locations (incluyendo
// scripted_aliases, location_type, permit_status, cost_*, availability_*, etc.).
// Pasado un objeto parcial, hace UPDATE selectivo.
export async function updateLocation(
  locationId: string,
  patch: Record<string, unknown>,
) {
  const { error } = await supabase
    .from('locations')
    .update(patch)
    .eq('id', locationId);
  if (error) throw new Error(error.message);
}

export async function createLocation(
  jobId: string,
  data: { name: string; scripted_aliases?: string[]; source?: string; [k: string]: unknown },
): Promise<{ id: string }> {
  const { data: row, error } = await supabase
    .from('locations')
    .insert({
      job_id: jobId,
      scripted_aliases: data.scripted_aliases ?? [],
      source: data.source ?? 'user',
      status: 'draft',
      confidence: 1.0,
      ...data,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return { id: row.id as string };
}

export async function deleteLocation(locationId: string) {
  // ON DELETE SET NULL en scenes.location_id mantiene las escenas — solo se
  // desvinculan. Si el usuario quiere borrar también los slug-lines, eso
  // se hace por separado.
  const { error } = await supabase
    .from('locations')
    .delete()
    .eq('id', locationId);
  if (error) throw new Error(error.message);
}

// Merge: traslada las escenas de `sourceIds` a `targetId`, fusiona aliases y
// borra las localizaciones fuente. Operación atómica conceptualmente; aquí
// se hace en secuencia (4-5 calls). RLS permite ambos updates desde anon.
export async function mergeLocations(targetId: string, sourceIds: string[]) {
  // 1. Fetch target + sources
  const { data: target, error: e1 } = await supabase
    .from('locations')
    .select('id,name,scripted_aliases')
    .eq('id', targetId)
    .single();
  if (e1) throw new Error(e1.message);
  const { data: sources, error: e2 } = await supabase
    .from('locations')
    .select('id,name,scripted_aliases')
    .in('id', sourceIds);
  if (e2) throw new Error(e2.message);

  // 2. Reasignar escenas
  const { error: e3 } = await supabase
    .from('scenes')
    .update({ location_id: targetId })
    .in('location_id', sourceIds);
  if (e3) throw new Error(e3.message);

  // 3. Fusionar aliases (de-duplicado)
  const merged = new Set<string>([
    ...((target.scripted_aliases as string[]) ?? []),
    ...((sources ?? []).flatMap((s) => [s.name, ...((s.scripted_aliases as string[]) ?? [])])),
  ]);
  // Excluir el propio name del target del array de aliases
  merged.delete(target.name);
  const { error: e4 } = await supabase
    .from('locations')
    .update({ scripted_aliases: [...merged] })
    .eq('id', targetId);
  if (e4) throw new Error(e4.message);

  // 4. Borrar las fuentes
  const { error: e5 } = await supabase
    .from('locations')
    .delete()
    .in('id', sourceIds);
  if (e5) throw new Error(e5.message);
}

// Asigna una escena (o varias) a una localización real
export async function assignScenesToLocation(sceneIds: string[], locationId: string | null) {
  const { error } = await supabase
    .from('scenes')
    .update({ location_id: locationId })
    .in('id', sceneIds);
  if (error) throw new Error(error.message);
}

// === New Phase C-E queries ===

export async function fetchNarrativeAnalysis(jobId: string) {
  const { data, error } = await supabase
    .from('narrative_analysis')
    .select('*')
    .eq('job_id', jobId)
    .single();
  if (error && error.code !== 'PGRST116') throw new Error(error.message);
  return data;
}

/**
 * De dónde viene una escritura sobre una ficha de casting.
 *
 * Hace falta distinguirlo porque el re-análisis respeta lo que ha decidido una persona y
 * corrige lo demás. Si un proceso automático marcara sus escrituras como humanas, el
 * productor perdería correcciones que hoy funcionan solas: el caso claro es
 * `shooting_days`, que CastingPage auto-sincroniza desde el plan de rodaje y
 * estimate-budget-smart reescribe desde el DOOD.
 */
export type OrigenEdicion = 'persona' | 'proceso';

// Columnas que tiene sentido marcar como editadas a mano. Es el espejo del CHECK
// `casting_profiles_edited_fields_columnas` de la base: si aquí se colara una columna que
// allí no está (los tres campos de candidatos, por ejemplo), la base rechazaría el guardado
// entero del productor. Filtrar aquí evita que un campo nuevo tumbe un guardado; el CHECK
// queda de red por si el filtro se queda corto.
const CAMPOS_MARCABLES = new Set([
  'character_name', 'age_min', 'age_max', 'gender', 'acting_level',
  'skills', 'shooting_days', 'reference_profile', 'complexity_flags',
]);

export async function updateCastingProfile(id: string, updates: {
  character_name?: string;
  age_min?: number;
  age_max?: number;
  gender?: string;
  acting_level?: string;
  shooting_days?: number;
  skills?: string[];
  reference_profile?: string;
  complexity_flags?: string[];
  candidates?: unknown[] | null;
  candidates_summary?: string | null;
  candidates_at?: string | null;
}, origen: OrigenEdicion = 'persona') {
  // El defecto es 'persona' a propósito, y la dirección importa: si algún día aparece un
  // camino de escritura nuevo y a quien lo escriba se le olvida declarar el origen, lo que
  // pasa es que el análisis deja de corregir un campo (queda algo desactualizado, se
  // arregla editándolo). Al revés, el olvido silenciaría una edición humana y el trabajo se
  // perdería. Entre quedarse corto y destruir, se elige quedarse corto.
  const payload: Record<string, unknown> = { ...updates };
  if (origen === 'persona') {
    // Esto es una PROPUESTA, no la marca final. El formulario de la ficha manda siempre
    // sus ocho campos, haya tocado el productor uno o los ocho, así que aquí no se sabe
    // qué ha cambiado de verdad. Quien lo sabe es la base, que ve el valor viejo y el
    // nuevo: el trigger `casting_profiles_unir_edited_fields` se queda solo con los que
    // cambian y los une a los que ya hubiera. Sin ese filtro, corregir una errata en el
    // perfil de referencia congelaría de paso la edad y el género.
    const marcados = Object.keys(updates).filter((c) => CAMPOS_MARCABLES.has(c));
    if (marcados.length > 0) payload.edited_fields = marcados;
  }

  const { data, error } = await supabase
    .from('casting_profiles')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Guarda la última tanda de candidatos de `research-casting` en el perfil.
 *
 * Existe porque cada búsqueda cuesta dinero de verdad (una llamada al modelo
 * más N consultas a TMDB) y antes el resultado vivía solo en el estado de React
 * del diálogo: al cerrarlo se perdía y había que pagar la búsqueda otra vez.
 *
 * Devuelve la marca de tiempo guardada para que la interfaz pueda pintar
 * "buscados el ..." sin esperar al refetch.
 */
export async function saveCastingCandidates(
  profileId: string,
  candidates: unknown[],
  summary: string,
): Promise<string> {
  const at = new Date().toISOString();
  // 'proceso': el productor pidió la búsqueda, pero lo que se guarda aquí lo escribió
  // research-casting, no él. Ninguna de estas tres columnas es marcable de todos modos,
  // así que declararlo es sobre todo dejar la intención por escrito.
  await updateCastingProfile(profileId, {
    candidates,
    candidates_summary: summary || null,
    candidates_at: at,
  }, 'proceso');
  return at;
}

export async function fetchCharacterArcs(jobId: string) {
  const { data, error } = await supabase
    .from('character_arcs')
    .select('*')
    .eq('job_id', jobId)
    .order('presence_pct', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchEmotionalMap(jobId: string) {
  const { data, error } = await supabase
    .from('emotional_map')
    .select('*')
    .eq('job_id', jobId)
    .order('scene_number', { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchCastingProfiles(jobId: string) {
  const { data, error } = await supabase
    .from('casting_profiles')
    .select('*')
    .eq('job_id', jobId);
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchBudgetEstimate(jobId: string) {
  const { data, error } = await supabase
    .from('budget_estimates')
    .select('*')
    .eq('job_id', jobId)
    .single();
  if (error && error.code !== 'PGRST116') throw new Error(error.message);
  return data;
}

export async function fetchDistributionStrategy(jobId: string) {
  const { data, error } = await supabase
    .from('distribution_strategy')
    .select('*')
    .eq('job_id', jobId)
    .single();
  if (error && error.code !== 'PGRST116') throw new Error(error.message);
  return data;
}

export async function fetchThemes(jobId: string) {
  const { data, error } = await supabase
    .from('themes')
    .select('*')
    .eq('job_id', jobId);
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchDiagnostics(jobId: string) {
  const { data, error } = await supabase
    .from('narrative_diagnostics')
    .select('*')
    .eq('job_id', jobId);
  if (error) throw new Error(error.message);
  return data || [];
}

// === Budget Lines CRUD ===

export async function fetchBudgetLines(jobId: string) {
  return fetchAllJobBudgetLines(jobId);
}

export async function createBudgetLine(line: {
  job_id: string;
  capitulo: number;
  capitulo_nombre: string;
  partida: string;
  concepto: string;
  unidades: number;
  precio_unitario: number;
  total: number;
  notas?: string;
  sub_section?: string;
  unit_type?: string;
  irpf_pct?: number;
  contract_type?: string;
  country_code?: string;
  person_name?: string;
}) {
  // The DB table has both Spanish (capitulo, concepto) and English (chapter, concept) columns
  // The English ones have NOT NULL constraints from the original schema
  const { data, error } = await supabase
    .from('budget_lines')
    .insert({
      ...line,
      chapter: line.capitulo,
      concept: line.concepto,
      units: line.unidades,
      unit_price: line.precio_unitario,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateBudgetLine(id: string, updates: {
  unidades?: number;
  precio_unitario?: number;
  total?: number;
  concepto?: string;
  notas?: string;
  person_name?: string;
  total_units?: number;
  agency_pct?: number;
  irpf_pct?: number;
  irpf_amount?: number;
  ss_amount?: number;
  dietas?: number;
  rod_days?: number;
  unit_type?: string;
  // Quien factura (autonomo | sociedad | cesion_derechos) no genera SS de empresa, así que
  // cambiar esto recalcula `ss_amount` y obliga a recuadrar la agregada 10.02.01.
  contract_type?: string;
  pre_weeks?: number;
  rod_weeks?: number;
  post_weeks?: number;
}) {
  const { data, error } = await supabase
    .from('budget_lines')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteBudgetLine(id: string) {
  const { error } = await supabase
    .from('budget_lines')
    .delete()
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function seedBudgetFromTariffs(jobId: string, tariffs: Array<{
  capitulo: number;
  capitulo_nombre: string;
  partida: string;
  concepto: string;
  unidades: number;
  precio_unitario: number;
  total: number;
  sub_section?: string;
  unit_type?: string;
  irpf_pct?: number;
  contract_type?: string;
  country_code?: string;
  person_name?: string;
  total_units?: number;
  agency_pct?: number;
  irpf_amount?: number;
  ss_amount?: number;
  dietas?: number;
  rod_days?: number;
  pre_weeks?: number;
  rod_weeks?: number;
  post_weeks?: number;
}>) {
  // Snapshot current lines before destructive overwrite (for undo).
  const prevLines = await fetchAllJobBudgetLines(jobId);
  if (prevLines && prevLines.length > 0) {
    await supabase.from('project_content').upsert(
      {
        job_id: jobId,
        content_type: 'budget_snapshot_undo',
        content: { lines: prevLines, taken_at: new Date().toISOString() },
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'job_id,content_type' },
    );
  }
  // Delete existing lines for this job first
  await supabase.from('budget_lines').delete().eq('job_id', jobId);
  // Insert all tariff lines (include both Spanish and English column names)
  const lines = tariffs.map(t => ({
    ...t,
    job_id: jobId,
    chapter: t.capitulo,
    concept: t.concepto,
    units: t.unidades,
    unit_price: t.precio_unitario,
  }));
  const { error } = await supabase.from('budget_lines').insert(lines);
  if (error) throw new Error(error.message);
}

export async function undoLastBudgetSeed(jobId: string) {
  const { data: snap } = await supabase
    .from('project_content')
    .select('content')
    .eq('job_id', jobId)
    .eq('content_type', 'budget_snapshot_undo')
    .maybeSingle();
  const stored = snap?.content as { lines?: Record<string, unknown>[] } | null;
  if (!stored?.lines || stored.lines.length === 0) {
    throw new Error('No hay un estado anterior que restaurar');
  }
  await supabase.from('budget_lines').delete().eq('job_id', jobId);
  const restored = stored.lines.map((l) => {
    // Strip server-managed fields so insert generates fresh ids/timestamps.
    const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = l as Record<string, unknown>;
    return rest;
  });
  const { error } = await supabase.from('budget_lines').insert(restored);
  if (error) throw new Error(error.message);
  // Snapshot is single-shot — clear after restore.
  await supabase.from('project_content')
    .delete()
    .eq('job_id', jobId)
    .eq('content_type', 'budget_snapshot_undo');
}

export async function hasBudgetSnapshot(jobId: string): Promise<boolean> {
  const { count } = await supabase
    .from('project_content')
    .select('id', { count: 'exact', head: true })
    .eq('job_id', jobId)
    .eq('content_type', 'budget_snapshot_undo');
  return (count ?? 0) > 0;
}

export async function fetchConflicts(jobId: string) {
  const { data, error } = await supabase
    .from('conflicts')
    .select('*')
    .eq('job_id', jobId);
  if (error) throw new Error(error.message);
  return data || [];
}

// === Stage Status ===
export async function fetchStageStatuses(jobId: string) {
  const { data, error } = await supabase
    .from('stage_status')
    .select('*')
    .eq('job_id', jobId);
  if (error) throw new Error(error.message);
  return data || [];
}

export async function updateStageStatus(jobId: string, stage: string, status: string) {
  const { data, error } = await supabase
    .from('stage_status')
    .upsert(
      { job_id: jobId, stage, status, completed_at: status === 'completed' ? new Date().toISOString() : null },
      { onConflict: 'job_id,stage' }
    )
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

// Señales de datos por etapa para el estado AUTOMÁTICO del dashboard. Conteos
// en paralelo + content_types de project_content. Resiliente: cualquier query
// que falle (RLS, tabla sin job_id) cuenta como 0.
export async function fetchStageSignals(jobId: string): Promise<StageSignals> {
  const countOf = async (table: string): Promise<number> => {
    try {
      const { count, error } = await supabase
        .from(table)
        .select('*', { count: 'exact', head: true })
        .eq('job_id', jobId);
      if (error) return 0;
      return count || 0;
    } catch {
      return 0;
    }
  };
  const [scenes, narrative, moodboard, locations, casting, budgetLines, productionPoints, distribution, contentRows] =
    await Promise.all([
      countOf('scenes'),
      countOf('narrative_analysis'),
      countOf('moodboard_items'),
      countOf('locations'),
      countOf('casting_profiles'),
      countOf('budget_lines'),
      countOf('production_points'),
      countOf('distribution_strategy'),
      supabase.from('project_content').select('content_type').eq('job_id', jobId),
    ]);
  const contentTypes = new Set<string>(((contentRows as any).data || []).map((r: any) => r.content_type));
  return { scenes, narrative, moodboard, locations, casting, budgetLines, productionPoints, distribution, contentTypes };
}

export async function initializeStages(jobId: string) {
  const stages = ['narrativa', 'moodboard', 'rodaje', 'casting', 'presupuesto', 'financiacion', 'audiencias'];
  const rows = stages.map(stage => ({
    job_id: jobId,
    stage,
    status: stage === 'narrativa' ? 'in_progress' : (['moodboard', 'casting', 'audiencias'].includes(stage) ? 'available' : 'locked'),
  }));
  const { error } = await supabase.from('stage_status').upsert(rows, { onConflict: 'job_id,stage' });
  if (error) throw new Error(error.message);
}

// === Budget Brief ===
export async function fetchBudgetBrief(jobId: string) {
  const { data } = await supabase
    .from('project_content')
    .select('content,updated_at')
    .eq('job_id', jobId)
    .eq('content_type', 'budget_brief')
    .maybeSingle();
  if (!data) return null;
  // content stored as JSON string OR object (depending on Supabase config)
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = typeof data.content === 'string' ? JSON.parse(data.content) : (data.content as Record<string, unknown>);
  } catch { parsed = null; }
  return parsed ? { ...parsed, updated_at: data.updated_at } : null;
}

export async function saveBudgetBrief(jobId: string, brief: Record<string, unknown>) {
  const { error } = await supabase.from('project_content').upsert(
    {
      job_id: jobId,
      content_type: 'budget_brief',
      content: JSON.stringify(brief),
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'job_id,content_type' },
  );
  if (error) throw new Error(error.message);
}

// === Project Content ===
export async function fetchProjectContent(jobId: string) {
  const { data, error } = await supabase
    .from('project_content')
    .select('*')
    .eq('job_id', jobId);
  if (error) throw new Error(error.message);
  return data || [];
}

export async function upsertProjectContent(jobId: string, contentType: string, content: string, params?: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('project_content')
    .upsert(
      { job_id: jobId, content_type: contentType, content, generation_params: params || null, updated_at: new Date().toISOString() },
      { onConflict: 'job_id,content_type' }
    )
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

// Merge atómico de UNA entrada en el mapa location_regions. Evita el race de
// read-modify-write del blob (ubicar dos localizaciones seguidas perdía una): cada
// llamada (1) lee la fila MÁS RECIENTE de la BD antes de mezclar y (2) se encadena
// con un candado de proceso, así nunca parte de un snapshot viejo ni se pisan.
let regionWriteChain: Promise<unknown> = Promise.resolve();
export function mergeLocationRegion(jobId: string, key: string, value: Record<string, unknown>): Promise<unknown> {
  const run = async () => {
    const { data: row } = await supabase
      .from('project_content')
      .select('content')
      .eq('job_id', jobId)
      .eq('content_type', 'location_regions')
      .maybeSingle();
    let regions: Record<string, unknown> = {};
    try {
      const c = (row as { content?: unknown } | null)?.content;
      const parsed = typeof c === 'string' ? JSON.parse(c) : (c || {});
      regions = (parsed?.regions as Record<string, unknown>) || {};
    } catch { regions = {}; }
    regions[key] = value;
    return upsertProjectContent(jobId, 'location_regions', JSON.stringify({ regions, updated_at: new Date().toISOString() }));
  };
  // run en ambas ramas: un fallo previo no rompe la cadena de las siguientes.
  regionWriteChain = regionWriteChain.then(run, run);
  return regionWriteChain;
}

// === Chat Messages ===
export async function fetchChatMessages(jobId: string, stage: string) {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('job_id', jobId)
    .eq('stage', stage)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

// ─── Chat experto en STREAMING (Fase 1 · agent-runtime Node, SSE) ────────────
// Habla con el endpoint Node (600s, sin techo de cuelgue) y va emitiendo eventos
// en directo. El servidor inserta el mensaje de usuario y persiste el del
// asistente (igual que la edge), así que el cliente solo refresca al terminar.
const AGENT_RUNTIME_URL =
  (import.meta.env.VITE_AGENT_RUNTIME_URL as string | undefined) ||
  'https://pelip-agent-runtime.vercel.app';

export type ExpertChatEvent =
  | { type: 'text'; delta: string }
  | { type: 'action'; op: string; table: string; status: 'applied' | 'pending' }
  | { type: 'consult'; role: string }
  | { type: 'done'; message: string; actions?: unknown[]; persisted?: boolean; applied?: number }
  | { type: 'error'; message: string };

export async function streamExpertChat(
  jobId: string,
  stage: string,
  content: string,
  onEvent: (e: ExpertChatEvent) => void,
  userName?: string,
): Promise<void> {
  // El runtime Node exige JWT de usuario (cierra el IDOR: valida propiedad del job).
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('No autenticado');
  const res = await fetch(`${AGENT_RUNTIME_URL}/api/expert-chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ job_id: jobId, stage, message: content, user_name: userName }),
  });
  if (!res.ok || !res.body) throw new Error(`expert-chat ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith('data:')) continue;
      try { onEvent(JSON.parse(line.slice(5).trim()) as ExpertChatEvent); } catch { /* ignora líneas parciales */ }
    }
  }
}

export async function sendChatMessage(jobId: string, stage: string, content: string, userName?: string) {
  await supabase.from('chat_messages').insert({ job_id: jobId, stage, role: 'user', content, user_name: userName ?? null });

  // El agente aplica cambios en un bucle síncrono dentro de la edge function.
  // Un cambio muy grande encadena varios turnos y excede el límite de tiempo del
  // edge (~150s): la petición se queda colgada sin respuesta. Ponemos un timeout
  // de cliente para no dejar el chat girando para siempre y guiar al usuario a
  // trocear la petición. (Arreglo de fondo pendiente: mover los cambios gordos a
  // un pipeline en background con polling.)
  let data!: { message: string; actions?: unknown; auto_applied?: boolean; persisted?: boolean };
  try {
    const res = await withTimeout(
      supabase.functions.invoke('chat-expert', {
        body: { job_id: jobId, stage, message: content, user_name: userName },
      }),
      170_000,
    );
    if (res.error) throw new Error(res.error.message || 'Error del agente experto');
    data = res.data;
  } catch (e) {
    const isTimeout = e instanceof Error && e.message.startsWith('timeout');
    if (!isTimeout) throw e;
    const aviso =
      'Ese cambio es demasiado grande para aplicarlo de una vez por aquí. ' +
      'Pídemelo por partes (por ejemplo, un capítulo o una partida a la vez) y lo voy aplicando en varias tandas.';
    const timeoutMsg = {
      job_id: jobId,
      stage,
      role: 'assistant' as const,
      content: aviso,
      actions: null,
      actions_applied: false,
    };
    await supabase.from('chat_messages').insert(timeoutMsg);
    return { message: aviso, actions: null, auto_applied: false, timed_out: true };
  }

  // El edge function ya persiste el mensaje del asistente con service_role
  // (durable, aunque el cliente falle). Si lo hizo (persisted:true), NO lo
  // insertamos otra vez aquí para no duplicar. Solo insertamos como fallback
  // cuando el backend es antiguo y no lo persistió.
  if (!data.persisted) {
    const assistantMsg = {
      job_id: jobId,
      stage,
      role: 'assistant' as const,
      content: data.message,
      actions: data.actions || null,
      // Si el backend auto-aplicó las acciones, marcamos el mensaje como ya
      // aplicado para que el ChatPanel muestre badge en vez de botón Aplicar.
      actions_applied: !!data.auto_applied,
    };
    await supabase.from('chat_messages').insert(assistantMsg);
  }
  return data;
}

// Sanitiza columnas alias que el LLM puede usar por error en inserts/updates.
// Mapea nombres alternativos a las columnas reales del schema.
const BUDGET_LINES_COLUMN_ALIASES: Record<string, string> = {
  codigo: 'partida',
  code: 'partida',
  line_code: 'partida',
  importe: 'total',
  amount: 'total',
  cantidad: 'unidades',
  agencia_pct: 'agency_pct',
  agencia: 'agency_pct',
  notes: 'notas',
};
const BUDGET_LINES_VALID_COLUMNS = new Set([
  'id', 'job_id', 'capitulo', 'capitulo_nombre', 'partida', 'concepto',
  'person_name', 'unidades', 'total_units', 'unit_type', 'precio_unitario',
  'total', 'pre_weeks', 'rod_weeks', 'post_weeks', 'agency_pct',
  'irpf_pct', 'irpf_amount', 'ss_amount', 'dietas', 'contract_type',
  'source', 'notas', 'agency_cost', 'created_at', 'updated_at',
  'chapter', 'concept', // legacy mirrors (NOT NULL on some installs)
]);

function sanitizeBudgetLineData(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    const mapped = BUDGET_LINES_COLUMN_ALIASES[k] ?? k;
    if (BUDGET_LINES_VALID_COLUMNS.has(mapped)) {
      out[mapped] = v;
    } else {
      console.warn(`[applyChatActions] descarte columna desconocida en budget_lines: ${k}`);
    }
  }
  // Mirror capitulo → chapter (legacy column) so inserts don't hit NOT NULL on
  // installs that still have the chapter column required.
  if (out.capitulo != null && out.chapter == null) {
    out.chapter = out.capitulo;
  }
  // Mirror concepto → concept (same legacy pattern)
  if (out.concepto != null && out.concept == null) {
    out.concept = out.concepto;
  }
  return out;
}

export async function applyChatActions(messageId: string, actions: Array<{ table: string; operation: string; id?: string; data?: Record<string, unknown> }>) {
  // Fetch params del job (necesarios para calcSS)
  // `content` entra aquí para el REGISTRO DE DECISIONES: el texto con el que el
  // Line Producer propuso el cambio es el porqué que el productor está aceptando
  // al pulsar Aplicar, y viaja hasta el `notas` de la línea servida.
  const { data: msgRow } = await supabase.from('chat_messages').select('job_id, content').eq('id', messageId).maybeSingle();
  const jobId = (msgRow as { job_id?: string } | null)?.job_id;
  const motivoDelChat = String((msgRow as { content?: unknown } | null)?.content ?? '');
  const { anotarDecision } = await import('@/lib/decisiones-registro');
  let paramsMap: Record<string, number> = {};
  if (jobId) {
    const { data: bp } = await supabase.from('budget_params').select('param_key,param_value').eq('job_id', jobId);
    for (const p of (bp || []) as Record<string, number>[]) paramsMap[String(p.param_key)] = Number(p.param_value);
  }
  paramsMap = applyLegalParams(paramsMap, await fetchLegalParamValues());

  const { calcBruto, calcIRPF, importeAgencia, ssAmountRecalculada } = await import('@/lib/budget-calc');

  // Accumulate errors for end-of-flow reporting (instead of silent failure)
  const errors: Array<{ action: typeof actions[number]; error: string }> = [];
  let applied = 0;
  // RASTRO PARA EL DESHACER. Cada escritura de budget_lines que sale de aquí deja
  // lo necesario para revertirla. Sin esto, el productor podía deshacer sus propias
  // ediciones de la tabla pero no las decenas de líneas que le aplicaba el agente,
  // que son las que de verdad le mueven el presupuesto.
  const subsLog: SubAccionLog[] = [];

  for (const action of actions) {
    try {
      if (action.operation === 'update' && action.id && action.data) {
        let data = { ...action.data };
        // La fila COMO ESTABA. Sirve para dos cosas: recalcular los derivados y,
        // si es una línea de presupuesto, saber con qué concepto la volverá a
        // generar el motor (la clave con la que se reencuentran las decisiones).
        let filaAntesDelCambio: Record<string, unknown> | null = null;

        if (action.table === 'budget_lines') {
          data = sanitizeBudgetLineData(data);
          // Fetch current row to recompute derived fields (total bruto, IRPF, SS).
          const { data: cur, error: fetchErr } = await supabase.from('budget_lines').select('*').eq('id', action.id).maybeSingle();
          if (fetchErr) throw new Error(`Lectura previa falló: ${fetchErr.message}`);
          if (!cur) throw new Error(`Línea ${action.id} no existe`);
          filaAntesDelCambio = cur as Record<string, unknown>;

          const merged: Record<string, any> = { ...cur, ...data };
          const cap = Number(merged.capitulo ?? 0);
          const unitType = String(merged.unit_type || 'T/A');

          // Cap 03 con SEM: recalcular total_units si cambian semanas
          if (cap === 3 && unitType === 'SEM') {
            const pre = Number(merged.pre_weeks || 0);
            const rod = Number(merged.rod_weeks || 0);
            const post = Number(merged.post_weeks || 0);
            merged.total_units = pre + rod + post;
            merged.unidades = merged.total_units;
            data.total_units = merged.total_units;
            data.unidades = merged.unidades;
          }

          // Recalcular SIEMPRE el total bruto si tocamos precio o unidades
          const priceTouched = data.precio_unitario !== undefined;
          const unitsTouched = data.total_units !== undefined || data.unidades !== undefined ||
            data.pre_weeks !== undefined || data.rod_weeks !== undefined || data.post_weeks !== undefined;
          if (priceTouched || unitsTouched) {
            const unitPrice = Number(merged.precio_unitario ?? 0);
            const totalUnits = Number(merged.total_units ?? merged.unidades ?? 1);
            const agencyPct = Number(merged.agency_pct ?? 0);
            merged.total = calcBruto(unitPrice, totalUnits);
            data.total = merged.total;
            merged.agency_cost = importeAgencia(merged.total, agencyPct);
            data.agency_cost = merged.agency_cost;
          }

          // Recalcular IRPF y SS si Cap 1-3 y cambió precio/units (o si total cambió)
          // El tipo de contrato decide si la línea cotiza, así que cambiarlo obliga a
          // recalcular la Seguridad Social aunque no se toque ni precio ni unidades. Sin
          // esto, "ponlo en sociedad" escribía la columna y dejaba la SS vieja, y el
          // recuadre de la 10.02.01 seguía sumando una cotización que ya no existe.
          const contractTouched = data.contract_type !== undefined;
          if (cap >= 1 && cap <= 3 && (priceTouched || unitsTouched || contractTouched || data.total !== undefined)) {
            const bruto = Number(merged.total ?? 0);
            const { pct, amount } = calcIRPF(bruto, cap, merged.irpf_pct !== undefined ? Number(merged.irpf_pct) : undefined);
            merged.irpf_pct = pct;
            merged.irpf_amount = amount;
            data.irpf_pct = pct;
            data.irpf_amount = amount;

            // SS empresa SOLO en personal (Cap 02/03). El Cap 01 (derechos/guion/música) es
            // cesión de derechos y NO cotiza; solo lleva IRPF de derechos de autor.
            merged.ss_amount = (cap === 2 || cap === 3) ? ssAmountRecalculada(merged, paramsMap, {
              unitPrice: Number(merged.precio_unitario ?? 0),
              totalUnits: Number(merged.total_units ?? merged.unidades ?? 1),
              capitulo: cap,
            }) : 0;
            data.ss_amount = merged.ss_amount;
          }

          // Si total quedó en 0, limpiar IRPF/SS/dietas residuales
          if (Number(merged.total ?? 0) === 0) {
            data.irpf_amount = 0;
            data.ss_amount = 0;
            data.dietas = 0;
          }
        }
        const { error: updErr } = await supabase.from(action.table).update(data).eq('id', action.id);
        if (updErr) throw new Error(updErr.message);
        if (action.table === 'budget_lines' && filaAntesDelCambio) {
          // Reverso = devolver SOLO las columnas tocadas a su valor anterior. La fila
          // entera no: si otra cosa la cambia mientras tanto, deshacer no debe pisarla.
          const antes: Record<string, unknown> = {};
          for (const k of Object.keys(data)) antes[k] = filaAntesDelCambio[k] ?? null;
          subsLog.push({ type: 'update', data: { id: String(action.id), before: antes } });
        }
        // El productor acaba de confirmar este cambio: se apunta como decisión suya
        // para que la próxima reestimación no se lo lleve por delante.
        if (action.table === 'budget_lines' && jobId) {
          await anotarDecision({
            jobId,
            filaAntes: filaAntesDelCambio,
            operacion: 'update',
            data: action.data,
            motivo: motivoDelChat,
            origenRef: messageId,
          });
        }
        applied++;
      } else if (action.operation === 'insert' && action.data) {
        const data = action.table === 'budget_lines'
          ? sanitizeBudgetLineData(action.data)
          : action.data;
        // Recalcular total/IRPF/SS también en INSERT. Antes solo se hacía en UPDATE,
        // así que las líneas AÑADIDAS por chat guardaban el ss_amount que el LLM
        // inventaba a ojo (p.ej. SS=15.000€) sin pasar por la normativa. Ahora se
        // deriva siempre con la misma fórmula (calcSS aplica tasa 33,27% + topes).
        if (action.table === 'budget_lines') {
          const cap = Number(data.capitulo ?? 0);
          const unitType = String(data.unit_type || 'T/A');
          const totalUnits = Number(data.total_units ?? data.unidades ?? 1);
          const agencyPct = Number(data.agency_pct ?? 0);
          // Si viene precio_unitario, recalcular el bruto desde precio×unidades(+agencia).
          // Si solo viene un total a tanto alzado, respetarlo.
          if (data.precio_unitario !== undefined) {
            data.total = calcBruto(Number(data.precio_unitario), totalUnits);
          }
          data.agency_cost = importeAgencia(Number(data.total ?? 0), agencyPct);
          if (cap >= 1 && cap <= 3) {
            const bruto = Number(data.total ?? 0);
            const { pct, amount } = calcIRPF(bruto, cap, data.irpf_pct !== undefined ? Number(data.irpf_pct) : undefined);
            data.irpf_pct = pct;
            data.irpf_amount = amount;
            // calcSS necesita precio unitario; si no vino, derivarlo del bruto/unidades.
            const unitPrice = data.precio_unitario !== undefined
              ? Number(data.precio_unitario)
              : (totalUnits > 0 ? bruto / totalUnits : 0);
            // SS empresa SOLO en personal (Cap 02/03). El Cap 01 (derechos) no cotiza.
            data.ss_amount = (cap === 2 || cap === 3)
              ? ssAmountRecalculada(data, paramsMap, { unitPrice, totalUnits, capitulo: cap })
              : 0;
          }
          // Si total quedó en 0, limpiar IRPF/SS/dietas residuales.
          if (Number(data.total ?? 0) === 0) {
            data.irpf_amount = 0;
            data.ss_amount = 0;
            data.dietas = 0;
          }
        }
        // En budget_lines se pide el id de vuelta: sin él, el reverso del alta no
        // sabe qué borrar y el Deshacer se queda corto justo en las líneas nuevas.
        if (action.table === 'budget_lines') {
          const { data: creada, error } = await supabase.from(action.table).insert(data).select('id').maybeSingle();
          if (error) throw new Error(error.message);
          const idCreada = (creada as { id?: string } | null)?.id;
          if (idCreada) subsLog.push({ type: 'insert', data: { id: String(idCreada) } });
        } else {
          const { error } = await supabase.from(action.table).insert(data);
          if (error) throw new Error(error.message);
        }
        if (action.table === 'budget_lines' && jobId) {
          // Línea que ha puesto el productor: si el motor no la genera en la
          // siguiente estimación, se repone.
          await anotarDecision({ jobId, filaAntes: null, operacion: 'insert', data, motivo: motivoDelChat, origenRef: messageId });
        }
        applied++;
      } else if (action.operation === 'delete' && action.id) {
        // Se lee ANTES de borrar: sin capítulo y concepto no hay forma de volver a
        // encontrar la línea después de una reestimación.
        let filaBorrada: Record<string, unknown> | null = null;
        if (action.table === 'budget_lines' && jobId) {
          const { data: prev } = await supabase.from('budget_lines').select('*').eq('id', action.id).maybeSingle();
          filaBorrada = (prev as Record<string, unknown> | null) ?? null;
        }
        if (action.table === 'locations') {
          // parent_id no tiene ON DELETE: sin esto, borrar una localización con sub-localizaciones fallaba.
          await supabase.from('locations').update({ parent_id: null }).eq('parent_id', action.id);
        }
        const { error } = await supabase.from(action.table).delete().eq('id', action.id);
        if (error) throw new Error(error.message);
        if (filaBorrada) subsLog.push({ type: 'delete', data: { line: filaBorrada } });
        if (filaBorrada && jobId) {
          await anotarDecision({ jobId, filaAntes: filaBorrada, operacion: 'delete', motivo: motivoDelChat, origenRef: messageId });
        }
        applied++;
      } else if (action.operation === 'upsert' && action.data) {
        const onConflict = action.table === 'budget_params' ? 'job_id,param_key' : undefined;
        const data = action.table === 'budget_lines'
          ? sanitizeBudgetLineData(action.data)
          : action.data;
        const { error } = await supabase.from(action.table).upsert(data, onConflict ? { onConflict } : {});
        if (error) throw new Error(error.message);
        applied++;
      } else {
        throw new Error(`Acción inválida (op=${action.operation}, id=${action.id ?? 'n/a'}, data=${action.data ? 'sí' : 'no'})`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error('[applyChatActions] action failed', { action, error: msg });
      errors.push({ action, error: msg });
    }
  }

  // Recuadre de la SS empresa: cualquier acción sobre budget_lines de Cap 02/03
  // (update/insert/delete) puede haber cambiado o quitado ss_amount, y la línea
  // agregada 10.02.01 del Cap 10 (la que suma la SS al total, una sola vez)
  // quedaría desincronizada. Se recuadra UNA vez tras aplicar todo el batch.
  if (jobId && applied > 0 && actions.some((a) => a.table === 'budget_lines')) {
    try {
      const { resyncSSEmpresaLine } = await import('@/lib/ss-empresa-resync');
      await resyncSSEmpresaLine(jobId);
    } catch (e) {
      console.error('[applyChatActions] recuadre SS empresa (10.02.01) falló', e);
    }
  }

  // RASTRO EN EL STACK DE DESHACER. Una sola entrada por tanda: deshacer lo que
  // aplicó el agente tiene que ser un gesto, igual que aplicarlo lo fue. Si la tanda
  // no tocó ninguna línea de presupuesto, no se ensucia el stack (solo caben 20).
  if (jobId) {
    const { logChatBudgetActions } = await import('@/services/budgetActionLog');
    await logChatBudgetActions(jobId, construirEntradaLogChat({ via: 'chat_boton', messageId, subs: subsLog }));
  }

  // ESTADO REAL DE LA TANDA. Antes se escribía `actions_applied: true` pasara lo que
  // pasara: el registro decía "aplicado" de tandas en las que habían fallado LAS
  // DIECISÉIS acciones, y el botón Aplicar desaparecía sin haber escrito nada. Ahora,
  // si no se aplicó ni un cambio, el mensaje queda como fallido y el botón sigue ahí.
  // Rechazar (lista vacía) sigue cerrando el mensaje, pero como 'rechazado', que es
  // lo que es: hasta hoy se contaba igual que un cambio aplicado.
  const resultado = { total: actions.length, aplicadas: applied, fallidas: errors.length };
  const { estado, actionsApplied } = estadoDeAplicacion(resultado);
  const marcado: Record<string, unknown> = {
    actions_applied: actionsApplied, actions_status: estado, actions_result: resultado,
  };
  const { error: markErr } = await supabase.from('chat_messages').update(marcado).eq('id', messageId);
  if (markErr) {
    // Reintento sin las columnas nuevas (migración 20260829200000): perder el marcado
    // dejaría el botón Aplicar activo sobre cambios que ya se aplicaron.
    console.warn('[applyChatActions] no pude marcar el estado de las acciones', markErr);
    await supabase.from('chat_messages').update({ actions_applied: actionsApplied }).eq('id', messageId);
  }

  // Si hubo errores: inyectar feedback como mensaje en el chat para que el
  // agente lo vea en el siguiente turno. Sin esto el modelo no aprende del
  // error y repite la misma propuesta inválida (ej: usar columna `city`).
  if (errors.length > 0 && jobId) {
    // Inferir stage desde el chat_message original
    const { data: ctxMsg } = await supabase
      .from('chat_messages')
      .select('stage')
      .eq('id', messageId)
      .maybeSingle();
    const stage = (ctxMsg as { stage?: string } | null)?.stage || 'narrativa';

    const summary = errors.slice(0, 15).map(({ action, error }) => {
      const tableCol = error.match(/'([^']+)' column/);
      const hint = tableCol
        ? ` → la columna "${tableCol[1]}" no existe en la tabla "${action.table}". Revisa el esquema en tu system prompt y usa una columna válida.`
        : '';
      return `· ${action.operation} ${action.table}${action.id ? ` #${String(action.id).slice(0, 8)}` : ''}: ${error}${hint}`;
    }).join('\n');
    const extra = errors.length > 15 ? `\n…y ${errors.length - 15} más con el mismo tipo de error.` : '';

    const feedbackMessage = `[SYSTEM · feedback automático tras intento de apply_changes]

Resultado: ${applied}/${actions.length} acciones aplicadas correctamente. ${errors.length} fallaron.

Errores:
${summary}${extra}

INSTRUCCIONES:
- NO repitas las mismas acciones con la misma columna inválida.
- Si una columna no existe, propón los campos válidos del esquema (ej: para guardar ciudad de una localización, usa "address" con la dirección completa).
- En tu próxima respuesta, corrige las acciones fallidas y NO repitas las que ya se aplicaron correctamente.`;

    await supabase.from('chat_messages').insert({
      job_id: jobId,
      stage,
      role: 'user',
      content: feedbackMessage,
      actions: null,
      actions_applied: true,
    });
  }

  if (errors.length > 0) {
    const lines = errors.slice(0, 5).map(({ action, error }) =>
      `· ${action.operation} ${action.table}${action.id ? ` #${String(action.id).slice(0, 8)}` : ''}: ${error}`
    ).join('\n');
    const extra = errors.length > 5 ? `\n…y ${errors.length - 5} más` : '';
    // El error viaja con el desglose ADJUNTO, no solo dentro del texto: la interfaz
    // necesita poder decir "5 de 16 aplicadas" en el título y el detalle debajo. El
    // toast genérico de antes ("Esta función no está disponible por ahora") le decía
    // al productor que no había pasado nada cuando le acababan de entrar 5 cambios.
    const e = new Error(`${resumenDeAplicacion(resultado)}\n${lines}${extra}`) as ErrorDeAplicacion;
    e.resultado = resultado;
    e.detalle = `${lines}${extra}`;
    throw e;
  }
  return resultado;
}

export async function generateContent(
  jobId: string,
  contentType: string,
  instructions?: string,
  destination?: string,
  formAnswers?: Record<string, string | string[]>,
  currentContent?: string
) {
  const { data, error } = await supabase.functions.invoke('generate-content', {
    body: { job_id: jobId, content_type: contentType, instructions: instructions || '', destination, form_answers: formAnswers, current_content: currentContent },
  });
  if (error) throw new Error(error.message || 'Error al generar contenido');
  return data;
}

// === Budget Params ===
export async function fetchBudgetParams(jobId: string) {
  const [{ data, error }, legal] = await Promise.all([
    supabase.from('budget_params').select('*').eq('job_id', jobId),
    fetchLegalParamValues(),
  ]);
  if (error) throw new Error(error.message);
  const map: Record<string, number> = {};
  for (const p of data || []) map[p.param_key] = Number(p.param_value);
  return applyLegalParams(map, legal);
}

export async function upsertBudgetParam(jobId: string, key: string, value: number) {
  const { error } = await supabase.from('budget_params').upsert(
    { job_id: jobId, param_key: key, param_value: value },
    { onConflict: 'job_id,param_key' }
  );
  if (error) throw new Error(error.message);
}

// === Budget Coproducers ===
export async function fetchBudgetCoproducers(jobId: string) {
  const { data, error } = await supabase.from('budget_coproducers')
    .select('*').eq('job_id', jobId).order('display_order');
  if (error) throw new Error(error.message);
  return data || [];
}

export async function addBudgetCoproducer(jobId: string, code: string, name: string, order: number) {
  const { error } = await supabase.from('budget_coproducers').insert({
    job_id: jobId, country_code: code, country_name: name, display_order: order,
  });
  if (error) throw new Error(error.message);
}

export async function removeBudgetCoproducer(id: string) {
  const { error } = await supabase.from('budget_coproducers').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

function getFileType(filename: string): 'pdf' | 'fdx' | 'fountain' {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'fdx') return 'fdx';
  if (ext === 'fountain' || ext === 'spmd') return 'fountain';
  return 'pdf';
}

// === Budget Scenarios ===

export async function fetchBudgetScenarios(jobId: string) {
  const { data, error } = await supabase
    .from('budget_scenarios')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function saveBudgetScenario(input: {
  jobId: string; name: string; shootingDays: number;
  lines: Record<string, unknown>[]; totals: Record<string, unknown>; financing: Record<string, unknown> | null;
}) {
  const { data, error } = await supabase.from('budget_scenarios').insert({
    job_id: input.jobId, name: input.name, shooting_days: input.shootingDays,
    lines: input.lines, totals: input.totals, financing: input.financing,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteBudgetScenario(id: string) {
  const { error } = await supabase.from('budget_scenarios').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// Promueve un escenario a oficial: snapshot de undo + reemplaza budget_lines +
// fija shooting_days. Mismo patrón destructivo que seedBudgetFromTariffs.
export async function promoteBudgetScenario(
  jobId: string,
  scenario: { id: string; shooting_days: number; lines: Record<string, unknown>[] },
) {
  const prevLines = await fetchAllJobBudgetLines(jobId);
  if (prevLines && prevLines.length > 0) {
    await supabase.from('project_content').upsert(
      { job_id: jobId, content_type: 'budget_snapshot_undo', content: { lines: prevLines, taken_at: new Date().toISOString() }, updated_at: new Date().toISOString() },
      { onConflict: 'job_id,content_type' },
    );
  }
  await supabase.from('budget_lines').delete().eq('job_id', jobId);
  const lines = (scenario.lines || []).map((l) => {
    const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = l as Record<string, unknown>;
    return { ...rest, job_id: jobId };
  });
  if (lines.length > 0) {
    const { error } = await supabase.from('budget_lines').insert(lines);
    if (error) throw new Error(error.message);
  }
  await supabase.from('budget_params').upsert(
    { job_id: jobId, param_key: 'shooting_days', param_value: scenario.shooting_days },
    { onConflict: 'job_id,param_key' },
  );
  await supabase.from('budget_scenarios').update({ promoted_at: new Date().toISOString() }).eq('id', scenario.id);
}
